import type { PropertyValueMap } from 'lit';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import type { ApplicationViewModel } from 'src/layout/application-view-model';
import { applyOrganizationLanguage } from 'src/i18n';
import { DShareContentDialog } from 'src/layout/parts/d-share-content-dialog';
import type { DPage } from 'src/pages/d-page';
import 'src/pages/d-page';
import 'src/content/d-content';
import 'src/layout/d-application-layout';
import { uuid } from 'src/utilities/text';
import '../content/employees/reassign-functions-dialog';
import '../library/fields/d-spinner-robot';
import type { DApplicationLayout } from './d-application-layout';
import './parts/file-viewer-dialog';
import './parts/d-top-submenu';
import './parts/d-new-document';
import type { DOrganizationHeader } from './parts/d-organization-header';
import './parts/d-organization-header';
import './parts/d-page-menu';
import './parts/d-robot-alert';
import './parts/d-chat-bubble';
import './parts/d-chat-window';
import './parts/d-app-update-banner';
import './parts/d-select-organization';
import '../library/editors/elements/d-select-checkbox';
import type { DNewDocument } from 'src/layout/parts/d-new-document';
import type { ContentView } from '../content/content-view';
import type { ContentEditDoneDetail } from '../content/content-edit-done-detail';
import type { SearchResult } from './parts/search-result';
import type { SettingsItem } from './parts/settings-item';
import type { HeaderSearchView } from './parts/header-search-view';
import type { HeaderSettingsView } from './parts/header-settings-view';
import type { ShareContentResult } from './parts/share-content-result';
import type { TopSubmenu } from 'src/layout/parts/top-submenu';
/**
 *
 *
 */
@customElement('d-application')
export class DApplication extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
      position: relative;
    }

    .content-dummy {
      display: block;
      position: relative;
      width: 100%;
      height: 100%;
      /* To prevent flickering when header is reduced */
      min-height: calc(100vh + 1px);
      background-color: white;
      box-shadow: 0 0 10px hsla(1, 0%, 0%, 0.3);
    }

    .content-dummy d-spinner-robot {
      max-height: 500px;
    }

    @media print {
      d-organization-header {
        display: none;
      }
    }
  `;
  @property({ type: Boolean })
  isAppPlatform = false;
  @property({ type: Array })
  searchResults: SearchResult[] = [];
  @property({ type: Boolean })
  offlineSearch = false;
  /**
   * No animation. Turn off all animations. Used by storybook to enable consistent snapshots.
   */
  @property({ type: Boolean, reflect: true, attribute: 'no-animation' })
  noAnimation = false;
  @property({ type: Object })
  user!: { username: string };
  @property({ type: Number })
  organizationId = 0;
  @property({ type: Object })
  organization: any = {};
  @property({ type: String })
  path = 'undefined';
  /**
   * The route path segments. The first element is the current pageId
   */
  @property({ type: Array })
  route: string[] = [];
  @property({ type: Boolean })
  active = false;
  @property({ type: Boolean })
  online = false;
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;
  @property({ type: Boolean })
  feedbackOpen = false;
  @property({ type: Boolean })
  issueOpen = false;
  @property({ type: Boolean })
  menuUncoveredSideWays = false;
  @property({ type: Object })
  applicationViewModel!: ApplicationViewModel;
  @property({ type: Number })
  appWidth = 0;
  @query('d-page')
  page?: DPage;
  @property({ type: String })
  searchQuery = '';
  @property({ type: Boolean })
  searchOpen = false;
  @property({ type: Boolean })
  settingsOpen = false;
  @property({ type: String })
  selectedSettings = 'profile';
  @property({ type: Number })
  scrollLeft = 0;
  @property({ type: Boolean })
  edit = false;
  @property({ type: Boolean })
  isUserEmergencyMode = false;
  @query('d-new-document')
  newDocumentElm!: DNewDocument;
  @property({ type: String })
  topSubmenu?: TopSubmenu;
  @property({ type: Number })
  topSubmenuPosition = 0;
  private saveNewMode = false;
  @state()
  private currentMode: 'edit' | 'new' | 'done' | 'normal' | 'diff' = 'normal';
  @query('d-application-layout')
  private _applicationLayoutEl!: DApplicationLayout;

  private get headerSearchView(): HeaderSearchView {
    return {
      type: 'search',
      query: this.searchQuery,
      results: this.searchResults,
      offlineSearch: this.offlineSearch,
      opened: true,
    };
  }

  private get headerSettingsView(): HeaderSettingsView {
    return {
      type: 'settings',
      user: {
        username: '',
        token: '',
        authDescription: '',
        criiptoAuthDescription: '',
      },
      isEmployee: false,
      alerts: 'NONE',
      receiveReminders: 'SMS',
      items: this.settingsItems,
    };
  }

  private get currentLevel() {
    if (this.route.length) {
      return (this.route.length + 1) / 2;
    }
    return 0;
  }

  private get settingsItems(): SettingsItem[] {
    if (this.applicationViewModel.loaded) {
      return this.applicationViewModel.settings;
    }
    return [];
  }

  private get isPageMenuCovered(): boolean {
    return (
      ((this.currentLevel > 0 && this.appWidth < 1024) ||
        (this.currentLevel > 1 && this.appWidth < 1220) ||
        (this.currentLevel > 4 && this.appWidth < 1280)) &&
      !this.menuUncoveredSideWays
    );
  }

  async onShareContent(e) {
    if (this.applicationViewModel.loaded) {
      const result: ShareContentResult = await DShareContentDialog.open({
        employees: this.applicationViewModel.employeesForShare,
        contacts: e.detail.contacts.length > 0 ? e.detail.contacts : this.applicationViewModel.contactsForShare,
        message: e.detail.message,
      });
      if (result.action === 'send') {
        const detail: { message: string; recipients: string[]; subject?: string; url?: string } = {
          message: result.message,
          recipients: result.recipients,
        };
        if (e.detail.subject) {
          detail.subject = e.detail.subject;
        }
        if (e.detail.url) {
          detail.url = e.detail.url;
        }
        this.dispatchEvent(
          new CustomEvent<{ message: string; recipients: string[]; subject?: string; url?: string }>('share', {
            bubbles: true,
            composed: true,
            detail,
          }),
        );
      }
    }
  }

  /**
   * Render each content view.
   *
   * @param contentView
   */
  renderContentView(contentView: ContentView, index: number, array: ContentView[], singleUserVersion) {
    const contentLevel = index + 2;
    let covered = false;
    if (this.currentLevel > contentLevel) {
      covered = true;
    }
    if (contentView && this.applicationViewModel.loaded) {
      return html`
        <d-content
          slot="level-${contentLevel}-content"
          id="level-${contentLevel}"
          .level=${contentLevel}
          .covered=${covered}
          .contentView=${contentView}
          .parentHref=${contentView.parentHref}
          .writeAccess=${contentView.currentUserHasAccess}
          .singleUserVersion=${singleUserVersion}
          .mode=${this.contentMode(index, array)}
          @share-content=${this.onShareContent}
          @content-edit-done=${(e: CustomEvent<ContentEditDoneDetail>) => {
            this.currentMode = 'normal';
            if (this.saveNewMode) {
              this.saveNewMode = false;
              e.stopPropagation();
              this.dispatchEvent(
                new CustomEvent<ContentEditDoneDetail>('content-edit-done', {
                  bubbles: true,
                  composed: true,
                  detail: {
                    ...e.detail,
                    uuid: uuid(),
                  },
                }),
              );
            }
          }}
          @open-new-document-slider=${() => this.newDocumentElm._toggleSlide()}
        ></d-content>
      `;
    }
    return html`
      <div slot="level-${contentLevel}-content" class="content-dummy">
        ${this.edit ? html` <d-spinner-robot></d-spinner-robot> ` : nothing}
      </div>
    `;
  }

  contentViewsWithDummies(contentViews) {
    const levels = [0, 1, 2, 3];
    return levels.map((level) => {
      if (contentViews[level]) {
        return contentViews[level];
      }
      return undefined;
    });
  }

  scrollHorizontal(e) {
    this._applicationLayoutEl.scrollHorizontal(e.detail.value, e.detail.behavior);
  }

  scrollStart(e) {
    this._applicationLayoutEl.scrollHorizontalStart(e.detail.direction);
  }

  scrollStop() {
    this._applicationLayoutEl.scrollHorizontalStop();
  }

  uncoverMenu() {
    if (this.applicationViewModel.loaded) {
      if (this.appWidth < 845) {
        this.dispatchEvent(
          new CustomEvent<{ href: string }>('navigate', {
            bubbles: true,
            composed: true,
            detail: { href: '/account/' + this.applicationViewModel.currentOrganizationId },
          }),
        );
      } else {
        if (this.scrollLeft > 0) {
          let type = 'scroll-horizontal';
          if (this.applicationViewModel.helpViewerOpen || this.applicationViewModel.tutorialViewerOpen) {
            type = 'jump-horizontal';
          }
          this.dispatchEvent(
            new CustomEvent<{ value: number }>(type, {
              composed: true,
              bubbles: true,
              detail: { value: 0 },
            }),
          );
          setTimeout(() => {
            this.menuUncoveredSideWays = true;
          }, 0);
        } else {
          this.menuUncoveredSideWays = true;
        }
      }
    }
  }

  onPageScrollLeftChanged(e) {
    this.scrollLeft = e.detail;
  }

  updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('isUserEmergencyMode')) {
      if (this.isUserEmergencyMode) {
        document.documentElement.style.backgroundColor = 'var(--emergency-background-color)';
        document.body.style.backgroundColor = 'var(--emergency-background-color)';
      } else {
        document.documentElement.style.backgroundColor = 'var(--app-background-color)';
        document.body.style.backgroundColor = 'var(--app-background-color)';
      }
    }
    if (_changedProperties.has('route')) {
      this.menuUncoveredSideWays = false;
    }
    // Språkvalg gjelder kun i org-er med hseProduct; avstem aktivt språk mot
    // gjeldende org når featureStates er kjent (norsk i org-er uten hseProduct).
    const viewModel = this.applicationViewModel;
    if (viewModel.loaded) {
      applyOrganizationLanguage(viewModel.currentOrganizationId, viewModel.featureStates.hseProduct);
    }
  }

  render() {
    if (this.isUserEmergencyMode) {
      return html`<div style="color: var(--emergency-accent-color)">emergency</div>`;
    }
    const viewModel = this.applicationViewModel;
    return viewModel.loaded
      ? html`
        <d-application-layout
          id="application-layout"
          .isAppPlatform=${this.isAppPlatform}
          .organizationId=${this.organizationId}
          .route=${this.route}
          .currentLevel=${this.currentLevel}
          .settings=${this.settingsOpen}
          ?edit=${this.edit}
          .uncoverMenu=${this.menuUncoveredSideWays}
          @app-width-changed=${(e: CustomEvent<{ appWidth: number }>) => (this.appWidth = e.detail.appWidth)}
          @open-search=${() => this.openSearch()}
          @scroll-left-changed=${(e) => this.onPageScrollLeftChanged(e)}
        >
          <d-organization-header
            slot="header"
            id="orgHeader"
            .menuCovered=${this.isPageMenuCovered}
            online="${this.online}"
            organization="${this.organization}"
            .organizationId=${this.organizationId}
            .currentOrganizationName=${viewModel.currentOrganizationName}
            .user="${this.user}"
            .currentUserIsOwner=${viewModel.currentUserIsOwner}
            .currentUserHasMultipleOrganizations=${viewModel.currentUserHasMultipleOrganizations}
            .writeAccess=${this.writeAccess}
            .displayedUserName=${viewModel.displayedUserName}
            .headerSearchView=${this.headerSearchView}
            .headerSettingsView=${this.headerSettingsView}
            .selectedSettings=${this.selectedSettings}
            .settingsOpen=${this.settingsOpen}
            .startTask=${viewModel.headerStartTask.startTask}
            .startTaskEvent=${viewModel.headerStartTask.relatedEvent}
            .currentEmployeeUuid=${viewModel.currentEmployeeUuid ?? ''}
            .startTaskExpanded=${viewModel.headerStartTask.startTaskExpanded}
            .userTotal=${viewModel.headerStartTask.userTotal}
            .userExecuted=${viewModel.headerStartTask.userExecuted}
            .singleUserVersion=${viewModel.singleUserVersion}
            .helpViewerOpen=${viewModel.helpViewerOpen}
            .tutorialViewerOpen=${viewModel.tutorialViewerOpen}
            .tutorialActive=${viewModel.tutorialActive}
            .featureStates=${viewModel.featureStates}
            .uiSettings=${viewModel.uiSettings}
            @header-height-changed=${this.onHeaderHeightChanged}
            @toggle-search=${(e) => this.toggleSearch(e)}
            @toggle-settings=${(e) => this.toggleSettings(e)}
            @open-top-submenu=${(e) => this.openTopSubmenu(e)}
            @uncover-menu=${() => this.uncoverMenu()}
          >
          </d-organization-header>
          <d-page-menu
            slot="menu"
            .organizationId=${viewModel.currentOrganizationId}
            .currentLevel=${this.currentLevel}
            .pageId=${viewModel.currentPageId}
            .pageMenu=${viewModel.pageMenu}
            .employeesCount=${viewModel.employeesCount}
            .specialTerms=${viewModel.specialTerms}
            ?covered=${this.currentLevel > 0}
            .featureStates=${viewModel.featureStates}
            .writeAccess=${this.writeAccess}
            .isAppPlatform=${this.isAppPlatform}
            .onlyPerformanceReview=${
              viewModel.featureStates.performanceReview &&
              !viewModel.featureStates.core &&
              !viewModel.featureStates.meetings &&
              !viewModel.featureStates.hse &&
              !viewModel.featureStates.staffing &&
              !viewModel.featureStates.assets &&
              !viewModel.featureStates.substances &&
              !viewModel.featureStates.issues &&
              !viewModel.featureStates.risk &&
              !viewModel.featureStates.data &&
              !viewModel.featureStates.quality &&
              !viewModel.featureStates.healthcare &&
              !viewModel.featureStates.collections
            }
          >
          </d-page-menu>
          <d-page
            id="d-page"
            slot="level-1-content"
            .pageView=${viewModel.currentPageView}
            .scrollLeft=${this.scrollLeft}
            .featureStates=${viewModel.featureStates}
            @share-content=${this.onShareContent}
            organization="${this.organization}"
            .user="${this.user}"
            .writeAccess=${this.writeAccess}
            .singleUserVersion=${viewModel.singleUserVersion}
            .appWidth=${this.appWidth}
            ?covered=${this.currentLevel > 1}
            ?fit-content=${viewModel.currentPageView.type === 'staffing-page'}
            @scroll-horizontal=${(e) => this.scrollHorizontal(e)}
            @scroll-start=${(e) => this.scrollStart(e)}
            @scroll-stop=${() => this.scrollStop()}
          ></d-page>
          ${this.contentViewsWithDummies(viewModel.contentViews).map((contentView, index, array) =>
            this.renderContentView(contentView, index, array, viewModel.singleUserVersion),
          )}
          <d-top-submenu
            slot="top-submenu"
            .submenu=${this.topSubmenu}
            .position=${this.topSubmenuPosition}
            .appWidth=${this.appWidth}
            .currentOrganizationName=${viewModel.currentOrganizationName}
            .displayedUserName=${viewModel.displayedUserName}
            .currentUserIsOwner=${viewModel.currentUserIsOwner}
            .currentUserHasMultipleOrganizations=${viewModel.currentUserHasMultipleOrganizations}
            .hseProduct=${viewModel.featureStates.hseProduct}
            .lastOrganizationsLoadedByUser=${viewModel.lastOrganizationsLoadedByUser}
            @open-settings=${(e) => this.openSettings(e)}
            @close-top-submenu=${() => this.closeTopSubmenu()}
          >
            <d-new-document
              slot="new-document"
              .appWidth=${this.appWidth}
              .writeAccess=${this.writeAccess}
              .currentUserHasEmergencyAccess=${viewModel.currentUserHasEmergencyAccess}
              .pageMenu=${viewModel.pageMenu}
              .currentPathArray=${viewModel.currentPathArray}
              .singleUser=${viewModel.singleUserVersion}
              .featureStates=${viewModel.featureStates}
              .userEmails=${viewModel.userEmails}
              .partners=${viewModel.partners}
              .defaultFunctionUuid=${viewModel.defaultFunctionUuid}
            ></d-new-document>
         </d-top-submenu>
        </d-application-layout>
        <d-app-update-banner></d-app-update-banner>
        <d-robot-alert
          .currentRobotAlert=${viewModel.currentRobotAlert}
          .functionsByTemplateId=${viewModel.functionsByTemplateId}
          .isAppPlatform=${this.isAppPlatform}
        ></d-robot-alert>
        ${
          viewModel.featureStates.matrix
            ? html` <d-chat-bubble
                  .unreadCount=${viewModel.matrixUnreadCount}
                  ?is-app-platform=${this.isAppPlatform}
                  ?emergency-visible=${viewModel.featureStates.emergency}
                ></d-chat-bubble>
                <d-chat-window
                  ?open=${viewModel.chatOpen}
                  ?expanded=${viewModel.chatExpanded}
                  ?ai-enabled=${viewModel.featureStates.ask}
                  ?is-app-platform=${this.isAppPlatform}
                  .mode=${viewModel.chatMode}
                  .organizationId=${viewModel.currentOrganizationId}
                  .organizationName=${viewModel.currentOrganizationName}
                  .currentUserName=${viewModel.displayedUserName}
                  .currentUserUuid=${viewModel.currentEmployeeUuid || ''}
                  .currentUserEmail=${viewModel.currentUserEmail}
                  .employees=${viewModel.employees}
                ></d-chat-window>`
            : nothing
        }
        </div>
      `
      : html` <d-spinner-robot ?no-animation=${this.noAnimation} size="60"></d-spinner-robot> `;
  }

  private onHeaderHeightChanged(e: CustomEvent<{ height: number }>) {
    if (this._applicationLayoutEl) {
      this._applicationLayoutEl.style.setProperty('--top-height', e.detail.height + 'px');
    }
  }

  private openSearch() {
    if (!this.searchOpen) {
      const header = this.shadowRoot?.getElementById('orgHeader') as DOrganizationHeader;
      if (header) {
        header.toggleSearch();
      }
    }
  }

  private toggleSearch(e) {
    this.searchOpen = e.detail.value;
    if (this.searchOpen) {
      this.settingsOpen = false;
    }
  }

  private toggleSettings(e) {
    this.settingsOpen = e.detail.value;
    if (this.settingsOpen) {
      this.searchOpen = false;
    }
  }

  private openSettings(e: CustomEvent<{ settingsType: string }>) {
    this.searchOpen = false;
    this.settingsOpen = true;
    this.selectedSettings = e.detail.settingsType;
  }

  private openTopSubmenu(e) {
    this.topSubmenu = e.detail.submenu;
    this.topSubmenuPosition = e.detail.position;
  }

  private closeTopSubmenu() {
    this.topSubmenu = undefined;
    this.topSubmenuPosition = 0;
  }

  private contentMode(index: number, array: ContentView[]) {
    return index === array.length - 1 ? this.currentMode : 'normal';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-application': DApplication;
  }
}
